/**
 * CMS 媒體庫（cms.media.manage 權限）。
 *
 * 景點的插槽掛載在 `./spots`，那邊會 import 本檔的 {@link MediaType}。
 */

import { API_BASE, ApiError, apiFetch, problemMessage, rawFetch, type Schemas } from './core';
import { clearAccessToken, getAccessToken } from '../auth';

export type MediaType = 'image' | 'video' | 'audio' | 'document';

export type MediaItem = Schemas['MediaResponseDto'];

/**
 * 列表回傳的素材：比 {@link MediaItem} 多一個 `usage`。
 *
 * 上傳與 PATCH 的回應沒有引用統計（後端不會為了回一列而多查一次），故兩個型別
 * 是分開的。凡是需要判斷「能不能刪」的地方都要用這個型別。
 */
export type MediaListItem = Schemas['MediaWithUsageResponseDto'];
export type MediaUsage = Schemas['MediaUsageDto'];
export type MediaUsageRef = Schemas['MediaUsageRefDto'];

export interface MediaListFilter {
  media_type?: MediaType;
  language?: string;
  /** 標籤精確比對（平面標記用）。 */
  tag?: string;
  /** 虛擬資料夾：命中該標籤本身與所有子孫。 */
  tag_prefix?: string;
  /** `true` = 只回完全沒有標籤者。序列化成字串送出。 */
  untagged?: boolean;
  q?: string;
  sort?: 'created_at' | 'filename';
  limit?: number;
  offset?: number;
}

export type TagFacet = Schemas['TagFacetDto'];
export type MediaTagFacets = Schemas['MediaTagFacetsResponseDto'];

/** 標籤統計；CMS 據此建虛擬資料夾樹。 */
export function listMediaTags(): Promise<MediaTagFacets> {
  return apiFetch<MediaTagFacets>('/admin/media/tags', { method: 'GET' });
}

export function listMedia(
  filter: MediaListFilter = {},
): Promise<{ items: MediaListItem[]; total: number }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    // `false` 一律省略（送 "false" 只是雜訊）；但不能用泛用的 falsy 判斷，
    // 那會把 limit: 0 / offset: 0 一起吃掉
    if (v === undefined || v === '' || v === false) continue;
    qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<{ items: MediaListItem[]; total: number }>(`/admin/media${suffix}`, {
    method: 'GET',
  });
}

export interface UploadResult {
  media: MediaItem;
  deduplicated: boolean;
}

/**
 * 上傳素材並回報進度。
 *
 * **為什麼用 XHR 而不是 fetch**：fetch 沒有上傳端的進度事件（ReadableStream
 * 上傳在多數瀏覽器仍未普及且需要 HTTP/2），要畫進度條只能走 XMLHttpRequest。
 *
 * 401 的處理必須與 {@link rawFetch} 一模一樣 —— 少了 `clearAccessToken()`
 * 會讓使用者帶著死 token 停在原地，其他請求都踢回登入、只有上傳不會，看起來
 * 像隨機故障。
 */
export function uploadMediaWithProgress(
  file: File,
  meta: { language?: string; tags?: string[] } = {},
  opts: { onProgress?: (loadedBytes: number) => void; signal?: AbortSignal } = {},
): Promise<UploadResult> {
  return new Promise<UploadResult>((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    if (meta.language) fd.append('language', meta.language);
    if (meta.tags?.length) fd.append('tags', meta.tags.join(','));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/admin/media`);
    xhr.withCredentials = true;
    xhr.responseType = 'text';
    // 不設 Content-Type —— 讓瀏覽器自行帶 multipart boundary
    xhr.setRequestHeader('Accept', 'application/json');
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    const onAbort = (): void => xhr.abort();
    opts.signal?.addEventListener('abort', onAbort);
    const cleanup = (): void => opts.signal?.removeEventListener('abort', onAbort);

    if (opts.onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) opts.onProgress?.(e.loaded);
      });
    }

    xhr.addEventListener('abort', () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    });

    xhr.addEventListener('error', () => {
      cleanup();
      reject(new ApiError(0, '網路錯誤，請確認連線後重試'));
    });

    xhr.addEventListener('load', () => {
      cleanup();
      const status = xhr.status;
      if (status === 401) {
        clearAccessToken();
        reject(new ApiError(401, problemMessage(parseBody(xhr.responseText), 401)));
        return;
      }
      if (status < 200 || status >= 300) {
        const problem = parseBody(xhr.responseText);
        reject(new ApiError(status, problemMessage(problem, status), problem));
        return;
      }
      const parsed = parseBody(xhr.responseText);
      if (!parsed) {
        reject(new ApiError(status, '伺服器回應無法解析'));
        return;
      }
      resolve(parsed as unknown as UploadResult);
    });

    xhr.send(fd);
  });
}

function parseBody(text: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

export function updateMedia(
  id: string,
  dto: { filename?: string; language?: string; tags?: string[] },
): Promise<MediaItem> {
  return apiFetch<MediaItem>(`/admin/media/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: dto,
  });
}

/**
 * 軟刪除素材。
 *
 * 仍被引用時後端回 409 `MEDIA_IN_USE`，`ApiError.problem.usage` 帶引用位置
 * （見 {@link usageOfError}）。`force` 用於明知有引用仍要刪的情況，後端會把
 * 稽核 severity 提升為 P2。
 */
export function deleteMedia(id: string, force = false): Promise<void> {
  return apiFetch<void>(`/admin/media/${encodeURIComponent(id)}${force ? '?force=true' : ''}`, {
    method: 'DELETE',
  });
}

/** 由 409 錯誤取出引用清單；不是 MEDIA_IN_USE 則回 null。 */
export function usageOfError(err: unknown): MediaUsageRef[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const usage = err.problem?.usage;
  return Array.isArray(usage) ? (usage as MediaUsageRef[]) : null;
}

/**
 * 取檔案內容並轉成 object URL 供 <img> / <video> 使用。
 *
 * 內容端點需要 Authorization header，`<img src>` 無法帶 header，故先 fetch
 * 成 blob。呼叫端務必於卸載時 `URL.revokeObjectURL`，否則影片預覽會累積佔用
 * 記憶體。
 */
export async function fetchMediaObjectUrl(id: string): Promise<string> {
  const res = await rawFetch(`/admin/media/${encodeURIComponent(id)}/content`);
  return URL.createObjectURL(await res.blob());
}
